import { useMemo, useRef } from 'react';
import { NJButton, NJHeading, NJText } from '@engie-group/fluid-design-system-react';
import type { FeatureCollection, Point } from 'geojson';
import type { LayerSpecification } from 'mapbox-gl';
import { type UMMapHandle } from '../../urbanmap/_components';
import { NODES } from './data';
import { effectiveConsumption, fmt } from './domain';
import { DecisionNote } from './DecisionPanel';
import { ProjectMap } from './ProjectMap';
import type { Building, Energy, Project } from './types';

export function BuildingList({ buildings, project, energy, selected, select, onDecision }: {
  buildings: Building[]; project: Project; energy: Energy; selected: string;
  select: (id: string) => void; onDecision: (id: string) => void;
}) {
  return <aside className="sp-navigation" aria-label="Bâtiments du projet">
    <NJText as="div" className="pu-label">Copie du projet · Gentilly</NJText>
    <NJHeading as="h2" scale="sm">Bâtiments ({buildings.length})</NJHeading>
    <NJText scale="sm">Liste accessible même sans carte. Données fictives de démonstration.</NJText>
    {buildings.map(b => <div className={`sp-tree-item ${selected === b.id ? 'is-active' : ''}`} key={b.id}>
      <NJButton label={b.name} emphasis="minimal" scale="sm" onClick={() => select(b.id)} />
      <NJText as="div" scale="sm">{fmt(effectiveConsumption(b, project, energy))} MWh/an
        {b.corrections[energy] !== undefined ? ' · corrigé' : ' · calculé'}{b.conflict ? ' · conflit UM' : ''}</NJText>
    </div>)}
    <DecisionNote id="layers" onOpen={onDecision} />
    <NJText scale="sm">Couches affichées : bâtiments du projet uniquement. Références UM en direct non activées.</NJText>
  </aside>;
}
export function DataMap({ buildings, project, energy, selected, select }: {
  buildings: Building[]; project: Project; energy: Energy; selected: string; select: (id: string) => void;
}) {
  const ref = useRef<UMMapHandle>(null);
  const sources = useMemo(() => {
    const buildingsSource: FeatureCollection<Point> = { type: 'FeatureCollection', features: buildings.map(b => ({
      type: 'Feature', properties: { id: b.id, demand: effectiveConsumption(b, project, energy), picked: selected === b.id ? 1 : 0 },
      geometry: { type: 'Point', coordinates: NODES[b.node] },
    })) };
    return { buildings: buildingsSource };
  }, [buildings, project, energy, selected]);
  const layers = useMemo<LayerSpecification[]>(() => [{
    id: 'pu-buildings', source: 'buildings', type: 'circle',
    paint: {
      'circle-radius': ['case', ['==', ['get', 'picked'], 1], 13, 9],
      'circle-color': ['step', ['get', 'demand'], '#ccd4da', 300, '#ef6b26', 600, '#c0d035', 1000, '#29a84a'],
      'circle-stroke-width': 2, 'circle-stroke-color': '#435c6e',
    },
  }], []);
  return <div className="sp-map" aria-label="Carte des bâtiments">
    <ProjectMap ref={ref} sources={sources} layers={layers} initialBounds={[[2.335, 48.804], [2.353, 48.816]]}
      interactiveLayerIds={['pu-buildings']} onFeatureClick={(_, f) => {
        if (typeof f.properties?.id === 'string') select(f.properties.id);
      }} />
    <div className="sp-map-title"><NJText as="div"><strong>Gentilly</strong> · {energy === 'heat' ? 'Chaleur' : 'Froid'}</NJText>
      <NJText scale="sm">Copie de démonstration · 16 octobre 2026 · points géographiques fictifs</NJText></div>
    <div className="sp-legend"><NJText as="div" scale="sm">Consommation effective · MWh/an</NJText>
      <NJText scale="sm">Gris &lt; 300 · orange 300–600 · vert clair 600–1 000 · vert ≥ 1 000</NJText></div>
  </div>;
}
